import { Component, ElementRef, input, OnDestroy, OnInit, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { getTimezonesWithOffsets } from '@vectronic/util';
import { startWith, Subscription } from 'rxjs';

@Component({
  selector: 'vectronic-timezone-picker',
  templateUrl: './timezone-picker.component.html',
  styleUrls: ['./timezone-picker.component.scss'],
  imports: [
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
  ],
})
export class TimezonePickerComponent implements OnInit, OnDestroy {
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  control = input<any>();

  disabled$ = toObservable(this.disabled);
  control$ = toObservable(this.control);

  disabledSubscription = this.disabled$.pipe(takeUntilDestroyed()).subscribe((disabled) => {
    if (disabled) {
      this.control()?.disable();
    } else {
      this.control()?.enable();
    }
  });

  controlSubscription = this.control$.pipe(takeUntilDestroyed()).subscribe((control) => {
    if (control) {
      this.subscription?.unsubscribe();
      this.subscription = this.control()
        ?.valueChanges.pipe(startWith(null))
        .subscribe((value: any) => {
          this.filteredOptions = value
            ? this._filter(this.timezones, value)
            : this.timezones?.slice();
        });
    }
  });

  timezones: any[];
  subscription: Subscription;
  filteredOptions: any[];

  timezoneInput = viewChild<ElementRef<HTMLInputElement>>('timezoneInput');
  timezoneAutocomplete = viewChild(MatAutocompleteTrigger);

  ngOnInit() {
    this.timezones = getTimezonesWithOffsets().map((zone) => this.formatZone(zone.name));
    this.filteredOptions = this.timezones?.slice();
    this.subscription = this.control()
      ?.valueChanges.pipe(startWith(null))
      .subscribe((value: any) => {
        this.filteredOptions = value ? this._filter(this.timezones, value) : this.timezones.slice();
      });
  }

  formatZone(zone: string) {
    return { value: zone };
  }

  private _filter(timezones: any[], value: string): any[] {
    if (typeof value !== 'string') return timezones.slice();
    const filterValue = value?.toLowerCase() || '';
    return timezones?.filter(({ value }) => value?.toLowerCase()?.includes(filterValue));
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  onClickRemove() {
    this.control()?.setValue('');
  }

  selectOption() {
    const newTimezone = this.filteredOptions?.[0];
    if (newTimezone) {
      this.control()?.setValue(newTimezone.value);
      const input = this.timezoneInput()?.nativeElement;
      if (input) input.value = newTimezone.value;
      this.timezoneAutocomplete()?.writeValue(newTimezone.value);
      this.timezoneAutocomplete()?.closePanel();
    }
  }
}
